import * as z from "zod";
import { isoDate } from "./common";

/**
 * The public shop directory: every shop we know of, grouped country → state →
 * city. Unauthenticated, and read by crawlers — most of these shops are
 * `IMPORTED` and unclaimed, so a directory page is the only page they have.
 *
 * Nothing here carries the scraped `sourceEmail`, on purpose: see the backend's
 * `DirectoryController`.
 */

/** A region or a city. `slug` is a permanent URL segment — renaming one discards an indexed page. */
export const DirectoryPlace = z.object({
  name: z.string(),
  slug: z.string(),
});
export type DirectoryPlace = z.output<typeof DirectoryPlace>;

/** A country. Its `slug` is the ISO code lower-cased, not the name. */
export const DirectoryCountry = z.object({
  code: z.string(),
  name: z.string(),
  slug: z.string(),
});
export type DirectoryCountry = z.output<typeof DirectoryCountry>;

/** A shop as a directory listing shows it. `slug` links to `/shop/<slug>`. */
export const DirectoryShop = z.object({
  slug: z.string(),
  name: z.string(),
  country: DirectoryCountry,
  /** Null in countries with no subdivision in the data — most of Europe. */
  region: DirectoryPlace.nullable(),
  city: DirectoryPlace.nullable(),
  /** The whole address as one display line. */
  address: z.string().nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
  websiteUrl: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  updatedAt: isoDate,
});
export type DirectoryShop = z.output<typeof DirectoryShop>;

/** Just enough to draw a pin: the country page frames a map over 800+ of these. */
export const DirectoryMapPin = z.object({
  slug: z.string(),
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
});
export type DirectoryMapPin = z.output<typeof DirectoryMapPin>;

export const DirectoryCountryGroup = z.object({
  value: DirectoryCountry,
  shopCount: z.number(),
});
export type DirectoryCountryGroup = z.output<typeof DirectoryCountryGroup>;

export const DirectoryPlaceGroup = z.object({
  value: DirectoryPlace,
  shopCount: z.number(),
});
export type DirectoryPlaceGroup = z.output<typeof DirectoryPlaceGroup>;

export const DirectoryRoot = z.object({
  countries: z.array(DirectoryCountryGroup),
  shopCount: z.number(),
  /** Every shop in the directory, as pins: the map a visitor sees before sharing a location. */
  pins: z.array(DirectoryMapPin),
});
export type DirectoryRoot = z.output<typeof DirectoryRoot>;

export const DirectoryCountryPage = z.object({
  country: DirectoryCountry,
  shopCount: z.number(),
  /** Empty in a country whose shops carry no province; then `cities` is the next level. */
  regions: z.array(DirectoryPlaceGroup),
  cities: z.array(DirectoryPlaceGroup),
  pins: z.array(DirectoryMapPin),
});
export type DirectoryCountryPage = z.output<typeof DirectoryCountryPage>;

export const DirectoryRegionPage = z.object({
  country: DirectoryCountry,
  region: DirectoryPlace,
  shopCount: z.number(),
  cities: z.array(DirectoryPlaceGroup),
  shops: z.array(DirectoryShop),
});
export type DirectoryRegionPage = z.output<typeof DirectoryRegionPage>;

export const DirectoryCityPage = z.object({
  country: DirectoryCountry,
  region: DirectoryPlace.nullable(),
  city: DirectoryPlace,
  shops: z.array(DirectoryShop),
});
export type DirectoryCityPage = z.output<typeof DirectoryCityPage>;

export const DirectoryShopLocation = z.object({
  name: z.string(),
  address: z.string().nullable(),
  /** Street lines only — schema.org/PostalAddress wants the parts apart. */
  street: z.string().nullable(),
  city: DirectoryPlace.nullable(),
  region: DirectoryPlace.nullable(),
  zip: z.string().nullable(),
  country: DirectoryCountry.nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});
export type DirectoryShopLocation = z.output<typeof DirectoryShopLocation>;

/** One shop's public page. `contactEmail` is what the merchant chose to publish, never the scraped address. */
export const DirectoryShopPage = z.object({
  storeId: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  logoUrl: z.string().nullable(),
  contactEmail: z.string().nullable(),
  returnsPolicy: z.string().nullable(),
  websiteUrl: z.string().nullable(),
  /** No user has claimed this shop yet — it came from the imported directory. */
  unclaimed: z.boolean(),
  canInviteOwner: z.boolean(),
  locations: z.array(DirectoryShopLocation),
  updatedAt: isoDate,
});
export type DirectoryShopPage = z.output<typeof DirectoryShopPage>;

export const DirectorySitemapCountry = z.object({
  country: DirectoryCountry,
  updatedAt: isoDate,
});
export type DirectorySitemapCountry = z.output<typeof DirectorySitemapCountry>;

export const DirectorySitemapRegion = z.object({
  country: DirectoryCountry,
  region: DirectoryPlace,
  updatedAt: isoDate,
});
export type DirectorySitemapRegion = z.output<typeof DirectorySitemapRegion>;

export const DirectorySitemapCity = z.object({
  country: DirectoryCountry,
  region: DirectoryPlace.nullable(),
  city: DirectoryPlace,
  updatedAt: isoDate,
});
export type DirectorySitemapCity = z.output<typeof DirectorySitemapCity>;

/** Places and shops, not URLs: the storefront owns its own routing. */
export const DirectorySitemap = z.object({
  countries: z.array(DirectorySitemapCountry),
  regions: z.array(DirectorySitemapRegion),
  cities: z.array(DirectorySitemapCity),
  shops: z.array(DirectoryShop),
});
export type DirectorySitemap = z.output<typeof DirectorySitemap>;
